import { useEffect, useContext, useMemo, useCallback, useRef } from "react";
import { useSession } from "next-auth/client";
import { useRouter } from "next/router";
import { t } from "@lingui/macro";
import { Box, Flex } from "@otrium/core";
import { AppCtx } from "src/contexts/app.context";
import { googleTagManager } from "src/tracking/googleTagManager";
import { localeFix } from "src/utils/genderLocale/genderLocale";
import {
  PopularCategory,
  HomepagePopularCategoryData,
  ProductLabelFilter,
  ImageBannerData,
  Homepage,
} from "src/types/graphql.d";
import { getI18nRoute } from "src/utils/i18nRoutes";
import { useNewInHomepage } from "src/hooks/useHomepage";
import { useIsLoggedIn } from "src/hooks/useIsLoggedIn";
import Error from "pages/_error";
import { Content, ContentMobileFullscreen } from "src/atoms/Content";
import { PageHead } from "src/molecules/PageHead";
import { PromotionBanner } from "src/molecules/PromotionBanner";
import { BrandCarousel } from "src/molecules/BrandCarousel";
import { ProductCarousel } from "src/molecules/ProductCarousel";
import { CategoryCarousel } from "src/molecules/CategoryCarousel";
import { CuratedCategory } from "src/molecules/CuratedCategory";
import { PromoBlock } from "src/molecules/PromoBlock";
import { RetroRevival } from "src/molecules/RetroRevival";
import { theme } from "src/theme";
import { SEGMENT_POSITIONS, SEGMENT_PROMOTION_TYPE } from "src/segment";
import { BrandsSection } from "src/organisms/BrandsSection";
import {
  SupportedKeys,
  SUPPORTED_KEYS,
} from "src/molecules/ExploreMoreCarousel/ExploreMoreCarousel";
import { AuthModule } from "../AuthModule";
import { RRPFootnote } from "src/molecules/RRPFootnote";
import { useLingui } from "@lingui/react";
import { HomePageType } from "src/types/HomePageType.d";
import { useFeatureFlags } from "src/hooks/useFeatureFlags";
import { filterExpiredMaketingBlocks } from "src/utils/filterExpiredImageBannerDeals";
import { usePageViewedContext } from "src/hooks/context/use-page-viewed-context";
import { useShopTypeContext } from "src/hooks/context/use-shop-type-context";
import { useOrganizationSchema } from "src/hooks/useOrganizationSchema";

type HomeViewProps = {
  homepage: Homepage;
};

const HomeView = ({ homepage }: HomeViewProps): JSX.Element => {
  const { i18n } = useLingui();
  const router = useRouter();
  const [session] = useSession();
  const { locale, setHomepageMounted, setPageViewedProperties } =
    useContext(AppCtx);
  const localeGenderId = useMemo(
    () => localeFix({ gender: router.query.slug as string }),
    [router.query.slug]
  );
  const { currentShopType } = useShopTypeContext();
  const isLoggedIn = useIsLoggedIn();
  const { pageViewTracked } = usePageViewedContext();
  const pageMountedRef = useRef(false);

  const genderId = router.query.slug as string;

  const { enableGatedHomepage } = useFeatureFlags();

  useEffect(() => {
    if (pageMountedRef.current) {
      return;
    }

    setHomepageMounted(true);
    setPageViewedProperties({
      page_type: "home",
    });

    pageMountedRef.current = true;
  }, [localeGenderId, setHomepageMounted, setPageViewedProperties]);

  useEffect(() => {
    if (session?.user.email && pageViewTracked) {
      googleTagManager.trackHomePage(session?.user.email);
    }
  }, [session?.user.email, pageViewTracked]);

  const { data: newInData, refetch: refetchNewIn } = useNewInHomepage({
    gender: localeGenderId,
    limit: 15,
    labels: [ProductLabelFilter.NewIn],
  });

  useEffect(() => {
    if (localeGenderId !== currentShopType) {
      try {
        void refetchNewIn();
      } catch (e) {
        console.error(e);
      }
    }
  }, [refetchNewIn, localeGenderId, currentShopType]);

  const promotionBannerSection = homepage?.PromotionBanners;
  const hasPromotionBanner =
    promotionBannerSection?.data?.length &&
    promotionBannerSection?.data?.length >= 1;
  const promotionBanner = promotionBannerSection?.data?.[0];
  const shops = homepage?.Shops;
  const department = homepage?.Departments?.[0];
  const categories = homepage?.Categories;
  const actionBlock = homepage?.ActionBlocks?.[0];
  const inspirationalSection = homepage?.InspirationalSections?.[0];
  const newIn = useMemo(
    () => newInData?.homepage?.productBlocks?.[0],
    [newInData?.homepage?.productBlocks]
  );
  const popularCategories = homepage?.PopularCategories?.[0];
  const marketingBlocks = homepage?.MarketingBlocks?.[0];

  const showSection = useCallback(
    (gated: boolean) => isLoggedIn || (enableGatedHomepage ? !gated : true),
    [isLoggedIn, enableGatedHomepage]
  );

  const description = t(
    i18n
  )`Explore all the fashion for ${genderId} at Otrium. Shop high-quality clothing, shoes, and accessories from well-known brands with discounts up to 70%!`;

  useOrganizationSchema(description);

  const brandsSectionPosition = useMemo(() => {
    const orders = [
      ...(shops ?? [])
        .filter((shop) => {
          if (shop.type === "featured_shops" && shop.data.length > 0) {
            return true;
          }

          return (
            shop.data.length >= 3 &&
            SUPPORTED_KEYS.includes(shop.type as SupportedKeys)
          );
        })
        .map((shop) => shop.position),
    ];

    if (department) {
      orders.push(department.position);
    }

    return Math.min(...orders);
  }, [shops, department]);

  const marketingBlocksData = useMemo(() => {
    const marketingData = marketingBlocks?.data || [];

    return marketingData
      .filter(
        ({ image_banners, position }) => image_banners?.length && position !== 0
      )
      .sort((a, b) => a.position - b.position)
      .map(({ image_banners }) => {
        const validBanners = filterExpiredMaketingBlocks(
          image_banners as ImageBannerData[]
        ).filter((item) => showSection(item?.gated ?? false));

        return validBanners[0];
      })
      .filter((banner) => banner !== undefined);
  }, [marketingBlocks?.data, showSection]);

  if (!homepage) {
    return <Error />;
  }

  const categoryCarouselData = popularCategories?.data.filter((i) =>
    Boolean(i.thumbnail)
  ) as PopularCategory[] | HomepagePopularCategoryData[];

  const showInspirationalSection =
    inspirationalSection &&
    inspirationalSection.data?.length &&
    showSection(inspirationalSection.data.every(({ gated }) => gated));

  return (
    <>
      <Box minHeight="100vh">
        <PageHead
          title={t(i18n)`Otrium | Online Designer Outlet`}
          deeplink={`otrium://${localeGenderId}`}
          description={description}
          enableIndexation
        />
        <Flex overflow="hidden" flexDirection="column" as="main">
          {!!hasPromotionBanner &&
          promotionBanner &&
          showSection(!!promotionBanner?.gated) ? (
            <Box
              pt={[0, 0, 0, 0, 0]}
              pb={[3, 3, 4, 4, 4]}
              mx={["-20px", "-20px", "-20px", 0, 0]}
              order={promotionBannerSection.position}
              as="section"
            >
              <Content>
                <PromotionBanner promotionBanner={promotionBanner} />
              </Content>
            </Box>
          ) : null}

          {!isLoggedIn && session && enableGatedHomepage ? (
            <Box
              pt={[0, 0, 0, 0, 0]}
              pb={[3, 3, 4, 4, 4]}
              mx={["-20px", "-20px", "-20px", 0, 0]}
              order={2}
              as="section"
            >
              <Content>
                <AuthModule
                  trackingData={{
                    pageType: HomePageType.GATED,
                    categorySlug: undefined,
                    shopType: localeGenderId,
                    pageName: `${localeGenderId} homepage`,
                  }}
                />
              </Content>
            </Box>
          ) : null}

          {marketingBlocks && marketingBlocksData.length > 2 ? (
            <Box
              key={marketingBlocks.position}
              pb={4}
              order={marketingBlocks.position}
            >
              <ContentMobileFullscreen noMarginBottom>
                <BrandCarousel
                  genderId={localeGenderId}
                  brands={marketingBlocksData}
                  slidesDesktop={3}
                  slidesTablet={3}
                  slidesMobile={1.5}
                  orderByNew={true}
                  disableTranslation={true}
                  hasVerticalPadding={false}
                  promotionType={SEGMENT_PROMOTION_TYPE.imageBanners}
                  pageName="homepage"
                />
              </ContentMobileFullscreen>
            </Box>
          ) : null}

          {shops ? (
            <BrandsSection
              data-testid="homepage_brand_section"
              key={localeGenderId} // key added intentionally. Forces remounting on the component on gender change
              order={
                isFinite(brandsSectionPosition) ? brandsSectionPosition : 0
              }
              shops={shops}
              department={department}
              genderId={localeGenderId}
            />
          ) : null}

          {categories
            ? categories.map((category) => {
                if (category.data.banner_image) {
                  return (
                    <Box
                      key={category.position}
                      py={5}
                      order={category.position}
                      as="section"
                    >
                      <ContentMobileFullscreen>
                        <CuratedCategory
                          genderId={genderId}
                          category={category.data}
                          slidesDesktop={6}
                          slidesTablet={3.5}
                          slidesMobile={2.99}
                          trackingData={{
                            shopType: localeGenderId,
                            pageName: "homepage",
                            carouselType: category.data.title,
                          }}
                        />
                      </ContentMobileFullscreen>
                    </Box>
                  );
                } else {
                  if (category.data.products) {
                    return (
                      <Box
                        key={category.position}
                        py={5}
                        order={category.position}
                        as="section"
                      >
                        <ContentMobileFullscreen>
                          <ProductCarousel
                            title={category.data.title}
                            subTitle={category.data.subtitle}
                            products={category.data.products}
                            headerLinkHref={
                              category.data.url
                                ? category.data.url
                                : `/collections/${genderId}/${category.data.slug}`
                            }
                            slidesDesktop={6}
                            slidesTablet={3.7}
                            slidesMobile={2.99}
                            zoomImageOnHover={true}
                            marginSizeMobile="l"
                            genderId={localeGenderId}
                            trackingData={{
                              shopType: localeGenderId,
                              pageName: "homepage",
                              carouselType: category.data.title,
                            }}
                          />
                        </ContentMobileFullscreen>
                      </Box>
                    );
                  } else {
                    return null;
                  }
                }
              })
            : null}

          {newIn?.data?.is_enabled && newIn?.data?.products?.length ? (
            <Box
              data-testid="homepage_new_in_section"
              key={newIn.position}
              order={newIn.position}
              bg="tone.sand"
              pt={["space24", "space24", "space32", "space64"]}
              pb={["space48", "space48", 40, 56]}
              as="section"
            >
              <ContentMobileFullscreen>
                <ProductCarousel
                  title={t(i18n)`New In`}
                  subTitle={
                    newIn?.data?.personalised
                      ? session?.user.name
                        ? t(i18n)`The latest styles for ${session?.user.name}`
                        : t(i18n)`The latest styles for you`
                      : t(i18n)`The latest (and greatest) styles`
                  }
                  products={newIn.data.products}
                  headerLinkHref={newIn.data.url}
                  slidesDesktop={5}
                  slidesTablet={3.5}
                  slidesMobile={2.99}
                  marginSizeMobile="l"
                  zoomImageOnHover={true}
                  showInfoText
                  overlayBgColor={theme.colors.tone.sand}
                  isPersonalised={newIn?.data?.personalised}
                  genderId={localeGenderId}
                  responseSourceType={newIn.data.response_source_type}
                  trackingData={{
                    shopType: localeGenderId,
                    pageName: "homepage",
                    carouselType: "New in",
                  }}
                />
              </ContentMobileFullscreen>
            </Box>
          ) : null}

          {actionBlock && !inspirationalSection ? (
            <Box
              py={5}
              mx={["-20px", "-20px", "-20px", 0, 0]}
              order={actionBlock.position}
              backgroundColor="tone.sand"
              as="section"
            >
              <Content>
                {actionBlock.data.code ? (
                  <PromoBlock
                    title={actionBlock.data.subtitle}
                    subtitle={actionBlock.data.title}
                    description={actionBlock.data.description}
                    code={actionBlock.data.code}
                    image={actionBlock.data.image}
                    position={SEGMENT_POSITIONS.actionBlock}
                    promotionType={SEGMENT_PROMOTION_TYPE.actionBlock}
                    gated={!!(!isLoggedIn && enableGatedHomepage)}
                  />
                ) : (
                  <PromoBlock
                    title={actionBlock.data.subtitle}
                    subtitle={actionBlock.data.title}
                    description={actionBlock.data.description}
                    link={actionBlock.data.button_link}
                    buttonText={actionBlock.data.button_title}
                    image={actionBlock.data.image}
                    position={SEGMENT_POSITIONS.actionBlock}
                    promotionType={SEGMENT_PROMOTION_TYPE.actionBlock}
                    gated={!!(!isLoggedIn && enableGatedHomepage)}
                  />
                )}
              </Content>
            </Box>
          ) : null}

          {showInspirationalSection ? (
            <Box order={inspirationalSection.position} as="section">
              <RetroRevival items={inspirationalSection.data} />
            </Box>
          ) : null}

          {popularCategories && showSection(!!popularCategories?.gated) ? (
            <Box
              pt={["space16", "space16", "space32", "space32"]}
              pb={["space16", "space16", "space32", "space32"]}
              order={popularCategories?.position}
              backgroundColor="tone.sand"
              as="section"
            >
              <ContentMobileFullscreen>
                <CategoryCarousel
                  genderId={localeGenderId}
                  title={t(i18n)`Popular categories`}
                  categories={categoryCarouselData}
                  color="tone.white"
                  headerLinkHref={`${getI18nRoute({
                    route: `/${localeGenderId}`,
                    locale,
                  })}/categories`}
                  slidesDesktop={categoryCarouselData.length}
                  slidesTablet={categoryCarouselData.length}
                  slidesMobile={2.5}
                />
              </ContentMobileFullscreen>
            </Box>
          ) : null}
        </Flex>
      </Box>
      <RRPFootnote />
    </>
  );
};

export default HomeView;
